import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { stripMarkdown } from '../utils/stripMarkdown';
import { SENTENCE_RE, splitTextIntoTtsChunks } from './ttsChunker';
import { useSettingsStore } from '../state/settings.store';
import { useDocTtsStore } from '../state/docTtsStore';

// Re-exported so existing importers of the chunker from this hook keep working.
export { splitTextIntoTtsChunks };


// settings-zustand-migration Phase 2: legacy keys 'voice-assistant-voice' and
// 'voice-assistant-tts-speed' migrated into the store's ttsVoiceId + ttsSpeed.
// The legacy keys are still pre-rehydration-migrated to the new store on first
// boot (see settings.store.ts runLegacyMigration).
const TTS_SPEED_STEPS = [0.75, 1, 1.25, 1.5, 1.75, 2];


/* ── Blob cache (LRU) ── */
const TTS_BLOB_CACHE_MAX = 40;
const ttsBlobCache = new Map<string, Blob>();

function ttsCacheGet(key: string): Blob | undefined {
  const b = ttsBlobCache.get(key);
  if (b) { ttsBlobCache.delete(key); ttsBlobCache.set(key, b); }
  return b;
}

function ttsCacheSet(key: string, blob: Blob) {
  if (ttsBlobCache.has(key)) ttsBlobCache.delete(key);
  ttsBlobCache.set(key, blob);
  while (ttsBlobCache.size > TTS_BLOB_CACHE_MAX) {
    const k = ttsBlobCache.keys().next().value;
    if (k === undefined) break;
    ttsBlobCache.delete(k);
  }
}

function clearTtsBlobCache() { ttsBlobCache.clear(); }

/**
 * Browser-native TTS mode (Protofac, SE-PF7). When the profile's `tts_provider` is
 * 'browser', read-aloud uses the client-side Web Speech API (`window.speechSynthesis`)
 * instead of fetching audio from the server Piper endpoint — zero server cost, no Cloud
 * Run TTS infra. Falls back to "no audio" if the browser lacks speechSynthesis (graceful;
 * mic STT is independent). The profile value is stashed transiently in the settings store
 * by App.tsx on profile load. */
function isBrowserTts(): boolean {
  try {
    return (
      useSettingsStore.getState().ttsProvider === 'browser' &&
      typeof window !== 'undefined' &&
      'speechSynthesis' in window
    );
  } catch {
    return false;
  }
}

function ttsCachePeek(key: string): boolean { return ttsBlobCache.has(key); }

/**
 * Read-aloud priority gate (FIX A). True while a docTtsStore read (chat-message
 * read-aloud OR the markdown doc viewer) is audibly active. When true, the shared
 * channel's streaming auto-read must NOT start — read-aloud has precedence, so a
 * new agent turn streaming in can't kill an in-progress read. This is the
 * suppression half of "read-aloud active ⇒ shared suppressed" (the explicit-start
 * half is ChatArea.onReadMessageAloud → sharedTts.stop() + the ReaderOverlay
 * rising-edge watcher). Together they keep the no-double-audio invariant.
 */
function isDocTtsReadActive(): boolean {
  try {
    const phase = useDocTtsStore.getState().phase;
    return phase !== 'idle' && phase !== 'stopped';
  } catch {
    return false;
  }
}

/* ── Prefetch tracking ── */
const prefetchControllers = new Set<AbortController>();
function cancelAllPrefetches() {
  for (const c of prefetchControllers) c.abort();
  prefetchControllers.clear();
}

export interface VoiceOption {
  id: string;
  name: string;
  gender: string;
  accent: string;
  description: string;
}

function loadSavedVoiceId(): string | null {
  return useSettingsStore.getState().ttsVoiceId;
}
function saveVoiceId(id: string) {
  useSettingsStore.getState().setTtsVoiceId(id);
}
function loadSpeed(): number {
  return useSettingsStore.getState().ttsSpeed;
}
function saveSpeed(speed: number) {
  useSettingsStore.getState().setTtsSpeed(speed);
}

// Revoke the blob URL from the audio element's src and reset it so buffered
// media data is released — critical for preventing memory pressure on mobile.
function clearAudioSrc(audio: HTMLAudioElement) {
  try {
    const src = audio.src;
    if (src?.startsWith('blob:')) URL.revokeObjectURL(src);
    audio.removeAttribute('src');
    audio.load(); // resets element to unloaded state, releasing buffered media
  } catch {}
}

/* ── Piper fetch ── */
async function fetchTtsBlob(text: string, voiceId: string, signal?: AbortSignal): Promise<Blob | null> {
  try {
    const clean = stripMarkdown(text).trim();
    if (!clean) return null;
    const cacheKey = `${voiceId}\0${clean}`;
    const cached = ttsCacheGet(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams({ text: clean, voice: voiceId });
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 15_000);
    signal?.addEventListener('abort', () => ctrl.abort(), { once: true });

    let res: Response;
    try {
      res = await fetch(`/api/tts/synthesize?${params}`, { method: 'POST', signal: ctrl.signal });
    } finally { clearTimeout(timeout); }
    if (!res.ok) return null;
    const blob = await res.blob();
    if (blob.size < 100) return null;
    ttsCacheSet(cacheKey, blob);
    return blob;
  } catch { return null; }
}

function prefetchTtsBlob(text: string, voiceId: string): void {
  const clean = stripMarkdown(text).trim();
  if (!clean) return;
  if (ttsCachePeek(`${voiceId}\0${clean}`)) return;
  const ctrl = new AbortController();
  prefetchControllers.add(ctrl);
  fetchTtsBlob(text, voiceId, ctrl.signal).finally(() => prefetchControllers.delete(ctrl));
}

/* ═══════════════════════════════════════════════════════════
 * useTTS — simplified TTS hook
 *
 * Single HTMLAudioElement (persistentAudioRef) is reused for every chunk.
 * This prevents O(n) Audio object accumulation on mobile which causes Chrome
 * to freeze or kill the tab under memory pressure.
 *
 * Cancellation: single `genRef` counter. Every speak()/stop() increments it.
 * The runner checks genRef before and after every await. If stale, it exits
 * without touching shared state (the new owner manages it).
 * ═══════════════════════════════════════════════════════════ */
export function useTTS() {
  const [speaking, setSpeaking] = useState(false);
  const [speakingSourceKey, setSpeakingSourceKey] = useState<string | null>(null);
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(loadSavedVoiceId);
  const [piperAvailable, setPiperAvailable] = useState(false);
  const [speed, setSpeedState] = useState(loadSpeed);

  /* ── Core refs ── */
  /** Generation counter — THE sole cancellation mechanism. */
  const genRef = useRef(0);
  /** Text chunks waiting to be spoken. */
  const queueRef = useRef<string[]>([]);
  /** True while the processQueue loop is active. */
  const runnerRef = useRef(false);
  /** The one persistent HTMLAudioElement, reused across all chunks to prevent memory churn. */
  const persistentAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Non-null only while a chunk is actively playing — the "is playing" sentinel. */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Resolves the current audio-play Promise (for immediate cancellation). */
  const audioResolveRef = useRef<(() => void) | null>(null);
  /** AbortController for the current in-flight Piper fetch. */
  const fetchAbortRef = useRef<AbortController | null>(null);
  /** Timestamp (ms) when the tab last became hidden — for debouncing visibility kill. */
  const hiddenAtRef = useRef(0);

  /* ── Streaming refs ── */
  const bufferRef = useRef('');
  /** True while a user-initiated speak() is active — blocks feedChunk/flush. */
  const userReplayRef = useRef(false);
  /** Set true by flush(); next feedChunk re-evaluates auto-read. */
  const streamNewRef = useRef(true);
  /** Locked at first chunk of each response: false if audio was playing → skip entire response. */
  const streamAutoReadRef = useRef(true);

  const selectedRef = useRef(selectedVoiceId);
  selectedRef.current = selectedVoiceId;
  const speedRef = useRef(speed);
  speedRef.current = speed;

  /* ── Per-chunk callback (Reader Mode sentence advances) ── */
  const chunkCallbackRef = useRef<((idx: number, text: string) => void) | null>(null);
  const chunkCounterRef = useRef(0);

  /* ── Lazy init of the persistent audio element ── */
  const getAudioEl = useCallback((): HTMLAudioElement => {
    if (!persistentAudioRef.current) {
      persistentAudioRef.current = new Audio();
    }
    return persistentAudioRef.current;
  }, []);

  /* ── Immediate audio teardown ── */
  const killAudio = useCallback(() => {
    fetchAbortRef.current?.abort();
    fetchAbortRef.current = null;
    // Browser-TTS mode: cancel any queued/active speechSynthesis utterance. Safe no-op
    // in Piper mode (no utterances). SE-PF7.
    try { window.speechSynthesis?.cancel(); } catch { /* unsupported */ }
    // done() clears audioRef.current; if it's not set, manually clear the element
    audioResolveRef.current?.();
    audioResolveRef.current = null;
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
      clearAudioSrc(a);
      audioRef.current = null;
    }
  }, []);

  /* ── Visibility recovery ── */
  // Chrome Android can freeze or silence audio during long backgrounds without setting
  // paused=true. Only intervene if the tab was hidden for > 5 seconds — short tab switches
  // (glancing at notifications, etc.) are normal mobile usage and killing the chunk makes
  // TTS feel choppy with no benefit.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible' && audioResolveRef.current) {
        if (Date.now() - hiddenAtRef.current > 5_000) killAudio();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [killAudio]);

  /* ── Cleanup persistent audio element on unmount ── */
  useEffect(() => {
    return () => {
      const el = persistentAudioRef.current;
      if (el) { el.pause(); clearAudioSrc(el); persistentAudioRef.current = null; }
    };
  }, []);

  /* ── Voice list ── */
  useEffect(() => {
    fetch('/api/tts/voices')
      .then((r) => r.ok ? r.json() : [])
      .then((data: VoiceOption[]) => {
        if (data.length > 0) {
          setVoices(data);
          setPiperAvailable(true);
          if (!loadSavedVoiceId()) {
            const def = data.find((v) => v.id === 'en_US-lessac-medium') || data[0];
            setSelectedVoiceId(def.id);
            saveVoiceId(def.id);
          }
        }
      })
      .catch(() => setPiperAvailable(false));
  }, []);

  const setVoice = useCallback((id: string) => {
    clearTtsBlobCache();
    setSelectedVoiceId(id);
    saveVoiceId(id);
  }, []);

  const setSpeed = useCallback((s: number) => { setSpeedState(s); saveSpeed(s); }, []);

  const cycleSpeed = useCallback(() => {
    const idx = TTS_SPEED_STEPS.indexOf(speedRef.current);
    const next = TTS_SPEED_STEPS[(idx + 1) % TTS_SPEED_STEPS.length];
    setSpeed(next);
    return next;
  }, [setSpeed]);

  /* ── Sequential runner ──
   * Only ONE runner is active at a time (guarded by runnerRef).
   * On generation mismatch it exits immediately WITHOUT touching runnerRef —
   * the new speak()/stop() owner already reset it.
   *
   * Issue #1136 fix — UI-state-truth-mismatch class:
   * `setSpeaking(true)` used to be set unconditionally at the top of the loop,
   * and `audio.play().catch(done)` silently swallowed autoplay/AudioContext
   * failures. When EVERY chunk failed to play, the UI showed the purple ▶/■
   * pill for the full fetch duration (sometimes minutes) without ever producing
   * sound, then quietly returned to idle. Now: we track whether *any* chunk
   * actually started playing (`anyChunkPlayed`) and whether a play() promise
   * rejected (`playRejected`). On first play() rejection we break the loop so
   * the user gets fast feedback ("nothing's coming — turn audio on / unmute")
   * rather than a silent UI lie that never matches reality. */
  const processQueue = useCallback(async () => {
    if (runnerRef.current) return;
    // Read-aloud priority (FIX A — CONTINUOUS gate, not the once-per-response latch).
    // processQueue is THE runner entry: speak()/enqueue()/feedChunk() all funnel here.
    // If a docTtsStore read-aloud is audibly active, the shared runner must NOT
    // (re)start — even if a streaming auto-read was already latched true before the
    // read began (ordering 2: stream latched/running, then user taps Read-aloud
    // mid-stream → sharedTts.stop() drops runnerRef but the latch survives → the next
    // feedChunk would otherwise enqueue + restart the runner CONCURRENTLY with the
    // active read = two audio channels). Drop the queued shared chunks so they can't
    // resume when the read ends. This is the airtight half: whatever slips past the
    // latch, the runner stays provably silent while a read-aloud is active.
    if (isDocTtsReadActive()) {
      queueRef.current = [];
      return;
    }
    runnerRef.current = true;
    const myGen = genRef.current;
    setSpeaking(true);
    // Track whether the *first* play() succeeded. If it didn't (autoplay block,
    // suspended AudioContext, etc.) we abort the loop instead of pretending.
    let anyChunkPlayed = false;
    let playRejected = false;

    while (queueRef.current.length > 0) {
      if (genRef.current !== myGen) return; // stale — exit silently

      const text = queueRef.current[0];
      const voiceId = selectedRef.current || 'en_US-lessac-medium';

      // Notify sentence-start callback (used by Reader Mode karaoke advance)
      chunkCallbackRef.current?.(chunkCounterRef.current++, text);

      // ── Browser-native TTS branch (Protofac, SE-PF7) ──
      // Narrate this chunk via window.speechSynthesis (client-side, zero server cost)
      // instead of fetching Piper audio. Mirrors the Piper path's gen-counter +
      // anyChunkPlayed/playRejected semantics so cancellation + the "nothing played"
      // fast-abort behave identically.
      if (isBrowserTts()) {
        queueRef.current.shift();
        const played = await new Promise<boolean>((resolve) => {
          let settled = false;
          const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            audioResolveRef.current = null;
            resolve(ok);
          };
          // killAudio()/stop() resolve via this ref (treated as "did not finish").
          audioResolveRef.current = () => finish(false);
          try {
            const u = new SpeechSynthesisUtterance(text);
            u.rate = Math.min(2, Math.max(0.5, speedRef.current));
            u.onend = () => finish(true);
            u.onerror = () => finish(false);
            window.speechSynthesis.speak(u);
          } catch {
            finish(false);
          }
        });
        if (genRef.current !== myGen) return; // interrupted during playback
        if (played) anyChunkPlayed = true; else playRejected = true;
        // Same fast-abort as the Piper path: if nothing has audibly played and a
        // chunk failed, stop pretending (autoplay block / no speechSynthesis voice).
        if (playRejected && !anyChunkPlayed) {
          queueRef.current = [];
          runnerRef.current = false;
          userReplayRef.current = false;
          setSpeaking(false);
          setSpeakingSourceKey(null);
          return;
        }
        continue;
      }

      // Prefetch next chunk while current one fetches/plays
      if (queueRef.current.length > 1) prefetchTtsBlob(queueRef.current[1], voiceId);

      // Fetch audio from Piper
      const ctrl = new AbortController();
      fetchAbortRef.current = ctrl;
      const blob = await fetchTtsBlob(text, voiceId, ctrl.signal);
      fetchAbortRef.current = null;

      if (genRef.current !== myGen) return; // interrupted during fetch

      queueRef.current.shift(); // consume after successful fetch

      if (!blob) continue; // Piper unavailable for this chunk — skip silently

      // Reuse the persistent audio element — prevents memory churn from N Audio objects
      const audio = getAudioEl();
      audio.pause();
      audio.currentTime = 0;
      clearAudioSrc(audio); // revoke previous blob URL and reset element
      const url = URL.createObjectURL(blob);
      audio.src = url;
      audio.playbackRate = speedRef.current;
      audioRef.current = audio;

      await new Promise<void>((resolve) => {
        let settled = false;
        let safetyTimer: ReturnType<typeof setTimeout>;
        // Track whether playback actually started — used to gate the stalled handler.
        // For blob URLs, stalled can fire before playing (during initial load) which would
        // incorrectly skip the chunk. Only skip on stalled if we were already playing.
        let hasStartedPlaying = false;

        const done = () => {
          if (settled) return;
          settled = true;
          clearTimeout(safetyTimer);
          audioResolveRef.current = null;
          audio.pause();
          clearAudioSrc(audio);
          if (audioRef.current === audio) audioRef.current = null;
          resolve();
        };
        audioResolveRef.current = done;
        // 30s safety — covers longest chunk (~960 chars) at 0.75x speed
        safetyTimer = setTimeout(done, 30_000);
        audio.addEventListener('ended', done, { once: true });
        audio.addEventListener('error', done, { once: true });
        audio.addEventListener('playing', () => {
          hasStartedPlaying = true;
          anyChunkPlayed = true;
        }, { once: true });
        // stalled fires when the browser stops fetching audio data unexpectedly.
        // Only skip if we were already playing — blob URLs can fire stalled during load
        // (Chrome power-save, audio context interruption) which is a false positive.
        audio.addEventListener('stalled', () => { if (hasStartedPlaying) done(); }, { once: true });
        // play() returns a Promise that rejects on:
        //   - NotAllowedError (autoplay policy / no user gesture)
        //   - NotSupportedError (unsupported codec / corrupt blob)
        //   - AbortError (interrupted before play completed)
        // Before #1136 we swallowed this silently and the UI lied. Now we
        // remember the rejection so the outer loop can abort instead of
        // looping through every chunk producing zero sound.
        audio.play().catch((err) => {
          // Log once so failures are debuggable without spamming on every chunk.
          if (!playRejected) {
            // eslint-disable-next-line no-console
            console.warn('[useTTS] audio.play() rejected — aborting TTS queue:', err);
          }
          playRejected = true;
          done();
        });
      });

      if (genRef.current !== myGen) return; // interrupted during playback

      // Issue #1136: if play() rejected (autoplay block, AudioContext suspended,
      // corrupt blob) and *nothing* has audibly played yet, the next chunk is
      // overwhelmingly likely to fail the same way. Drain the queue, clear UI
      // state, and stop pretending. The user sees the pill return to idle
      // immediately and can re-tap (which counts as a fresh user gesture).
      if (playRejected && !anyChunkPlayed) {
        queueRef.current = [];
        runnerRef.current = false;
        userReplayRef.current = false;
        setSpeaking(false);
        setSpeakingSourceKey(null);
        return;
      }
    }

    // Natural completion — we're still the current generation
    runnerRef.current = false;
    userReplayRef.current = false;
    setSpeaking(false);
    setSpeakingSourceKey(null);
  }, [getAudioEl]);

  /* ── Enqueue text and kick runner ── */
  const enqueue = useCallback((text: string) => {
    const clean = stripMarkdown(text).trim();
    if (!clean) return;
    queueRef.current.push(clean);
    processQueue();
  }, [processQueue]);

  /* ── Streaming: feed chunks as they arrive from WebSocket ── */
  const feedChunk = useCallback((chunk: string) => {
    if (userReplayRef.current) return; // user is replaying — don't interrupt

    // First chunk of a new response: decide once whether to auto-read.
    // Read-aloud priority (FIX A): if a docTtsStore read (chat-message read-aloud or
    // the doc viewer) is active, suppress streaming auto-read for this whole response
    // so the active read is never interrupted — and the two channels never overlap.
    if (streamNewRef.current) {
      streamNewRef.current = false;
      streamAutoReadRef.current = !runnerRef.current && !isDocTtsReadActive();
    }
    if (!streamAutoReadRef.current) return; // response started while audio was playing (or a read-aloud has priority)
    // Read-aloud priority (FIX A — re-check PAST the once-per-response latch). The
    // line above is the latch-once decision (only re-evaluated when streamNewRef is
    // true, i.e. at the first chunk of a response). It does NOT catch a read-aloud
    // that starts MID-STREAM: the stream is already latched true, so without this
    // re-check the next chunk would enqueue while a read is active. Stop feeding the
    // moment a read-aloud is audible — the runner's continuous gate is the backstop,
    // but cutting the enqueue here keeps the shared queue from filling behind it.
    if (isDocTtsReadActive()) return;

    bufferRef.current += chunk;
    let buf = bufferRef.current;
    let match = SENTENCE_RE.exec(buf);
    while (match) {
      const endIdx = match.index + match[0].length;
      enqueue(buf.slice(0, endIdx));
      buf = buf.slice(endIdx);
      match = SENTENCE_RE.exec(buf);
    }
    bufferRef.current = buf;
  }, [enqueue]);

  /* ── Flush remaining buffer (on CLAUDE_DONE) ── */
  const flush = useCallback(() => {
    streamNewRef.current = true;
    if (userReplayRef.current) { bufferRef.current = ''; return; }

    const remaining = bufferRef.current.trim();
    bufferRef.current = '';
    if (remaining) {
      const parts = splitTextIntoTtsChunks(remaining);
      for (const p of parts) enqueue(p);
    } else if (queueRef.current.length === 0 && !runnerRef.current) {
      setSpeaking(false);
    }
  }, [enqueue]);

  /* ── Speak a full message (replay, summary, etc.) ── */
  const speak = useCallback((text: string, sourceKey?: string | null) => {
    // Read-aloud priority chokepoint (FIX A). Removing the old ReaderOverlay
    // watcher ("shared starts → stop docTtsStore") deleted the safety net that used
    // to stop a read when the shared channel started. To keep "read-aloud active ⇒
    // shared suppressed" airtight for EVERY shared caller (play-latest, SSH
    // summary-ready, replay) — present and future — suppress speak() outright while
    // a docTtsStore read is active. This is the shared instance only (the doc viewer
    // + chat read-aloud ride docTtsStore, not this hook); when no read is active the
    // predicate is false and speak() proceeds normally.
    if (isDocTtsReadActive()) return;
    userReplayRef.current = (sourceKey !== undefined && sourceKey !== null);
    genRef.current++;
    cancelAllPrefetches();
    killAudio();
    queueRef.current = [];
    bufferRef.current = '';
    chunkCallbackRef.current = null;
    chunkCounterRef.current = 0;

    const clean = stripMarkdown(text).trim();
    if (!clean) {
      runnerRef.current = false;
      setSpeaking(false);
      setSpeakingSourceKey(null);
      return;
    }

    setSpeakingSourceKey(sourceKey ?? null);
    // Issue #1136: prime the persistent <audio> element NOW, while we're still
    // inside the click handler's user-gesture context. Otherwise lazy creation
    // happens after `await fetchTtsBlob(...)` and on iOS Safari the gesture
    // context is gone — first chunk's .play() rejects with NotAllowedError.
    getAudioEl();
    const chunks = splitTextIntoTtsChunks(text);
    const voiceId = selectedRef.current || 'en_US-lessac-medium';
    // Eagerly prefetch first two chunks
    if (chunks.length > 0) void fetchTtsBlob(chunks[0], voiceId);
    if (chunks.length > 1) prefetchTtsBlob(chunks[1], voiceId);

    for (const c of chunks) {
      const cl = stripMarkdown(c).trim();
      if (cl) queueRef.current.push(cl);
    }

    // Allow new runner to start (old runner exits on gen mismatch without touching runnerRef)
    runnerRef.current = false;
    processQueue();
  }, [killAudio, processQueue, getAudioEl]);

  /* ── Cancel an in-flight streaming auto-read immediately (read-aloud start) ──
   *
   * Read-aloud priority (FIX A — IMMEDIATE cancel half). When a chat read-aloud
   * starts (ChatArea.onReadMessageAloud), the already-running streaming auto-read
   * must stop feeding RIGHT NOW, not just on the next gate check. We clear the
   * once-per-response latch (so feedChunk's `!streamAutoReadRef` early-return fires
   * for the rest of this response) and bump genRef + killAudio so the live runner
   * exits on its next gen check and any playing chunk is silenced immediately.
   * We deliberately do NOT touch streamNewRef — flush() resets it to true on
   * CLAUDE_DONE, so the NEXT response re-decides auto-read cleanly; forcing it true
   * here would risk a mid-stream re-latch. Distinct from stop() (which is a full
   * teardown) so the continuous runner gate stays the independently-tested backstop. */
  const cancelStreamAutoRead = useCallback(() => {
    streamAutoReadRef.current = false;
    // True superset of stop(): clears EVERY ref stop() does so the shared instance
    // can't be left in a half-torn-down state. userReplayRef in particular is
    // load-bearing — a pill replay sets it true; if we left it true here, the next
    // feedChunk would `if (userReplayRef.current) return` and silently kill ALL
    // future streaming auto-read until some other speak()/stop() reset it (the
    // read-aloud toggle calls docTtsStore.stop, never this hook, so it never would).
    userReplayRef.current = false;
    genRef.current++;
    cancelAllPrefetches();
    killAudio();
    queueRef.current = [];
    bufferRef.current = '';
    chunkCallbackRef.current = null;
    chunkCounterRef.current = 0;
    runnerRef.current = false;
    setSpeaking(false);
    setSpeakingSourceKey(null);
  }, [killAudio]);

  /* ── Stop all playback ── */
  const stop = useCallback(() => {
    userReplayRef.current = false;
    genRef.current++;
    cancelAllPrefetches();
    killAudio();
    queueRef.current = [];
    bufferRef.current = '';
    chunkCallbackRef.current = null;
    chunkCounterRef.current = 0;
    runnerRef.current = false;
    setSpeaking(false);
    setSpeakingSourceKey(null);
  }, [killAudio]);

  /* ── Reader Mode: speak pre-split sentences with per-sentence callback ──
   *
   * Unlike speak() which splits text via splitTextIntoTtsChunks, this takes
   * an array of sentence strings (already tokenized by the caller) and an
   * optional callback fired BEFORE each sentence begins playing. This lets
   * ReaderOverlay advance its reading line in sync with TTS output.
   */
  const speakSentences = useCallback((
    sentences: string[],
    onSentenceStart?: (idx: number, text: string) => void,
  ) => {
    userReplayRef.current = true;
    genRef.current++;
    cancelAllPrefetches();
    killAudio();
    queueRef.current = [];
    bufferRef.current = '';
    chunkCallbackRef.current = onSentenceStart ?? null;
    chunkCounterRef.current = 0;

    const voiceId = selectedRef.current || 'en_US-lessac-medium';
    const filtered = sentences
      .map((s) => stripMarkdown(s).trim())
      .filter(Boolean);

    if (filtered.length === 0) {
      runnerRef.current = false;
      setSpeaking(false);
      setSpeakingSourceKey(null);
      chunkCallbackRef.current = null;
      return;
    }

    setSpeakingSourceKey('reader-mode');
    // Issue #1136: prime persistent <audio> inside the user-gesture context.
    getAudioEl();
    if (filtered[0]) void fetchTtsBlob(filtered[0], voiceId);
    if (filtered[1]) prefetchTtsBlob(filtered[1], voiceId);
    for (const s of filtered) queueRef.current.push(s);

    runnerRef.current = false;
    processQueue();
  }, [killAudio, processQueue, getAudioEl]);

  /* ── Voice preview ── */
  const previewVoice = useCallback(async (id: string) => {
    genRef.current++;
    cancelAllPrefetches();
    killAudio();
    queueRef.current = [];
    runnerRef.current = false;
    setSpeaking(false);
    setSpeakingSourceKey(null);

    const blob = await fetchTtsBlob('Hello! This is how I sound when reading responses.', id);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const audio = getAudioEl();
    audio.pause();
    audio.currentTime = 0;
    clearAudioSrc(audio);
    audio.src = url;
    audioRef.current = audio;
    const cleanup = () => {
      clearAudioSrc(audio);
      if (audioRef.current === audio) audioRef.current = null;
    };
    audio.addEventListener('ended', cleanup, { once: true });
    audio.addEventListener('error', cleanup, { once: true });
    audio.play().catch(cleanup);
  }, [killAudio, getAudioEl]);

  const selectedVoiceName = voices.find((v) => v.id === selectedVoiceId)?.name || 'Default';

  return {
    speaking, speakingSourceKey, speak, speakSentences, stop, cancelStreamAutoRead, feedChunk, flush,
    voices, selectedVoiceId, selectedVoiceName, setVoice, previewVoice,
    piperAvailable,
    speed, setSpeed, cycleSpeed,
  };
}
